import React, {useCallback, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import moment from "moment-timezone";
import Ebay from "../../component/shipping/Ebay";
import Shipped from "../../component/shipping/Shipped";
import ToShip from "../../component/shipping/ToShip";
import {Alert, Col, Container, Row} from "react-bootstrap";
import DatePicker from "react-datepicker";
import {
    useGetAllShippingList,
    useGetShippingDueList,
    useGetShippingEndingList,
    useGetShippingOrderList,
    useGetShippingPickedShelvingList,
} from "../../hooks/useShipping";
import "react-datepicker/dist/react-datepicker.css";
import "./UserDashboard.css";
import CustomDateRangeInput from "../../component/CustomDateRangeInput";

export default function UserDashboard() {
    const navigate = useNavigate();

    const [dateRange, setDateRange] = useState(() => {
        const today = moment().startOf("day").toDate();
        return [today, today];
    });

    const [startDate, endDate] = dateRange;

    const handleCalendarChange = useCallback(
        (dates) => {
            setDateRange(dates);
        },
        []
    );

    const setDateRangePreset = (preset) => {
        let start, end;
        const today = moment().startOf('day');

        switch (preset) {
            case 'last7':
                start = today.clone().subtract(6, 'days').toDate();
                end = today.clone().endOf('day').toDate();
                break;
            case 'thisMonth':
                start = today.clone().startOf('month').toDate();
                end = today.clone().endOf('month').toDate();
                break;
            case 'today':
            default:
                start = today.clone().toDate();
                end = today.clone().toDate();
                break;
        }
        setDateRange([start, end]);
    };


    const startDateString = useMemo(() => (startDate ? moment(startDate).format("YYYY-MM-DD") : ""), [startDate]);
    const endDateString = useMemo(() => (endDate ? moment(endDate).format("YYYY-MM-DD") : ""), [endDate]);

    const endingParams = useMemo(() => ({query: "listing"}), []);
    const dueParams = useMemo(() => ({query: "orderage", startDate: startDateString, endDate: endDateString}), [
        startDateString,
        endDateString,
    ]);
    const orderParams = useMemo(() => ({query: "order", startDate: startDateString, endDate: endDateString}), [
        startDateString,
        endDateString,
    ]);
    const pickedParams = useMemo(() => ({query: "picked", startDate: startDateString, endDate: endDateString}), [
        startDateString,
        endDateString,
    ]);
    const allShippingParams = useMemo(
        () => ({startDate: startDateString, endDate: endDateString}),
        [startDateString, endDateString]
    );

    const {data: endingSoonData, isFetching: isEndingSoonFetching} = useGetShippingEndingList(endingParams);
    const {data: dueData, isFetching: isDueFetching, isError: isDueError, error: dueError} = useGetShippingDueList(
        dueParams
    );
    const {
        data: orderData,
        isFetching: isOrderFetching,
        isError: isOrderError,
        error: orderError
    } = useGetShippingOrderList(
        orderParams
    );
    const {
        data: pickedData,
        isFetching: isPickedFetching,
        isError: isPickedError,
        error: pickedError,
    } = useGetShippingPickedShelvingList(pickedParams);
    const {
        data: allShippingList,
        isFetching: isShippingFetching,
        isError: isShippingError,
        error: shippingError,
    } = useGetAllShippingList(allShippingParams);

    const numberOfDays = useMemo(() => {
        if (!startDate || !endDate) return 0;
        return moment(endDate).diff(moment(startDate), "days") + 1;
    }, [startDate, endDate]);

    const handleNavigateBack = useCallback(() => navigate(-1), [navigate]);

    const isError = isDueError || isOrderError || isPickedError || isShippingError;
    const error = dueError || orderError || pickedError || shippingError;

    return (
        <Container fluid className="p-4">
            <Row className="mb-4 justify-content-between align-items-center">
                <Col xs="auto">
                    <i
                        onClick={handleNavigateBack}
                        className="fa-solid fa-arrow-left"
                        style={{cursor: "pointer", color: "white", fontSize: "1.5rem"}}
                    />
                </Col>
                <Col xs="auto" className="d-flex align-items-center">
                    <div className="date-controls">
                        <DatePicker
                            selectsRange={true}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={handleCalendarChange}
                            isClearable={false}
                            dateFormat="yyyy-MM-dd"
                            customInput={<CustomDateRangeInput startDate={startDate} endDate={endDate}/>}
                        />
                        <div>
                            <button type="button" className="quick-range-btn"
                                    onClick={() => setDateRangePreset('today')}>Today
                            </button>
                            <button type="button" className="quick-range-btn"
                                    onClick={() => setDateRangePreset('last7')}>Last 7 Days
                            </button>
                            <button type="button" className="quick-range-btn"
                                    onClick={() => setDateRangePreset('thisMonth')}>This Month
                            </button>
                        </div>
                    </div>
                </Col>
            </Row>

            {isError ? (
                <Alert variant="danger">
                    <Alert.Heading>Error Fetching Dashboard Data</Alert.Heading>
                    <p>There was a problem retrieving the data. Please try refreshing the page.</p>
                    <hr/>
                    <p className="mb-0">Error details: {error?.message}</p>
                </Alert>
            ) : (
                <Row lg={12}>
                    <Col lg={3} className="loading-wrapper">
                        <Ebay
                            dueData={dueData}
                            isDueDataLoading={isDueFetching}
                            endingData={endingSoonData}
                            isEndingDataLoading={isEndingSoonFetching}
                            orderData={orderData}
                            isOrderDataLoading={isOrderFetching}
                            pickedData={pickedData}
                            isPickedDataLoading={isPickedFetching}
                        />
                    </Col>
                    <Col lg={6} className="loading-wrapper">
                        <ToShip
                            startDate={startDate}
                            endDate={endDate}
                            allShippingList={allShippingList}
                            isShippingLoading={isShippingFetching}
                        />
                    </Col>
                    <Col lg={3} className="loading-wrapper">
                        <Shipped
                            allShippingList={allShippingList}
                            isShippingLoading={isShippingFetching}
                            numberOfDays={numberOfDays}
                        />
                    </Col>
                </Row>
            )}
        </Container>
    );
}